import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { generateSlug } from "../utils/seoUtils";
import "../pages/MetaInsight/topApps.css";
import { blogApiUrl } from "../utils/variables";
import PlatformTabs from "../components/Common/PlatformTabs";
import { usePlatforms } from "../contexts/PlatformsContext";

// Helper to generate page numbers with ellipsis (copied from dashboard)
function getPageNumbers(current, total) {
  const delta = 2;
  const range = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i);
  }
  if (current - delta > 2) {
    range.unshift("...");
  }
  if (current + delta < total - 1) {
    range.push("...");
  }
  range.unshift(1);
  if (total > 1) range.push(total);
  return Array.from(new Set(range)).filter(x => x <= total && x >= 1);
}

function CardSkeleton() {
  return (
    <div className="experience-card mb-3">
      <div className="skeleton" style={{ aspectRatio: "16/10", width: "100%" }} />
      <div className="card-body p-3">
        <div className="skeleton mb-2" style={{ height: 15, width: "80%", borderRadius: 4 }} />
        <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
          <div className="skeleton" style={{ height: 13, width: 40, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 13, width: 60, borderRadius: 4 }} />
        </div>
        <div className="skeleton" style={{ height: 12, width: "60%", marginTop: 8, borderRadius: 4 }} />
      </div>
    </div>
  );
}

export default function CategoryAllPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { getPlatformIcon, getPlatformDisplayName } = usePlatforms();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchParams] = useSearchParams();
  // Pre-select the platform passed from Category Explorer's "See All" (?platform=)
  const [platform, setPlatform] = useState(() => searchParams.get("platform") || null);

  // Single-select, same pattern as PlatformTabs elsewhere (e.g. the dashboard's
  // "Most Tracked Games" row) — clicking the already-active platform clears it
  // back to "all". Changing platform resets to page 1 like changing category does.
  const handlePlatformToggle = (platformId) => {
    setPlatform((current) => (current === platformId ? null : platformId));
    setPage(1);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page });
    if (platform) params.append("platform", platform);
    fetch(`${blogApiUrl}/tracker/explorer/category/${category}?${params.toString()}`)
      .then(async (res) => {
        // The backend returns a 404 "Category not found" for a category that
        // simply has no games on the selected platform (e.g. a genre that
        // hasn't been catalogued for Apple Vision yet) — that's an empty
        // result, not a broken request. Only a non-404 failure is a real
        // error; treating both the same surfaced a scary "Failed to fetch"
        // banner for what's really just "nothing here for this platform".
        if (res.status === 404) {
          setData([]);
          setTotal(0);
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message || "Failed to fetch category games");
        }
        const json = await res.json();
        setData(json.data || []);
        setTotal(json.total || 0);
        setPageSize(json.page_size || 20);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
      })
      .finally(() => setLoading(false));
  }, [category, page, platform]);

  return (
    <div className="categoryall-page" style={{ background: "#171821", minHeight: "100vh", paddingBottom: 40, paddingTop: 56 }}>
      <style>{`
        .skeleton {
          background: linear-gradient(90deg, #23242b 25%, #35363c 37%, #23242b 63%);
          background-size: 400% 100%;
          animation: skeleton-shimmer 1.4s ease infinite;
        }
        @keyframes skeleton-shimmer {
          0% { background-position: 100% 0 }
          100% { background-position: 0 0 }
        }
        
        .category-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }
        
        @media (max-width: 1200px) {
          .category-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        
        @media (max-width: 992px) {
          .category-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }
        }
        
        @media (max-width: 576px) {
          .category-grid {
            grid-template-columns: repeat(1, 1fr);
            gap: 16px;
          }
          .container {
            padding: 16px !important;
          }
          .topapps-header {
            font-size: 24px !important;
            margin-bottom: 20px !important;
          }
        }

        .experience-card {
          background: #23242b;
          border-radius: 20px;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: 1px solid rgba(255,255,255,0.05);
        }
        
        .experience-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.3);
          border-color: rgba(255,255,255,0.1);
        }

        .pagination-container {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 40px;
          flex-wrap: wrap;
          align-items: center;
        }

        .pagination-btn {
          min-width: 36px;
          height: 36px;
          padding: 0 12px;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.1);
          background: transparent;
          color: #fff;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.2);
        }

        .pagination-btn.active {
          background: #f6255a;
          border-color: #f6255a;
          color: #fff;
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.6);
          text-decoration: none;
          background: rgba(255,255,255,0.05);
          padding: 8px 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 24px;
          border: 1px solid rgba(255,255,255,0.08);
          transition: all 0.2s;
          cursor: pointer;
        }
        
        .back-btn:hover {
          color: #fff;
          background: rgba(255,255,255,0.1);
          border-color: rgba(255,255,255,0.15);
          transform: translateX(-4px);
        }

        .category-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }
      `}</style>
      <div className="container" style={{ maxWidth: 1240, margin: "0 auto", padding: "40px 24px 0" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, marginBottom: 32 }}>
          <h2 className="topapps-header" style={{ marginBottom: 0, fontWeight: 700, color: '#fff', fontSize: 32 }}>
            {category ? category.charAt(0).toUpperCase() + category.slice(1).toLowerCase() : ""} Games
          </h2>
          <button 
            className="back-btn" 
            onClick={() => navigate('/category-explorer')}
            aria-label="Back to Category Explorer"
            style={{ marginBottom: 0 }}
          >
            <span>&#8592;</span> Back
          </button>
        </div>
        <div style={{ marginBottom: 24 }}>
          <PlatformTabs
            variant="pill"
            showDivider={false}
            selected={platform ? [platform] : []}
            onToggle={handlePlatformToggle}
          />
        </div>
        {error ? (
          <div className="alert alert-danger mt-4">{error}</div>
        ) : !loading && data.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: "64px 24px",
              background: "rgba(255,255,255,0.02)",
              borderRadius: 20,
              border: "1px dashed rgba(255,255,255,0.1)",
            }}
          >
            <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: 8 }}>No games found</h6>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, maxWidth: 380, margin: "0 auto" }}>
              {platform
                ? `We don't have any ${category} games for ${getPlatformDisplayName(platform)} yet. Try another platform or check back soon.`
                : `We don't have any ${category} games yet. Check back soon.`}
            </p>
          </div>
        ) : (
          <>
            <div className="category-grid">
              {loading ? (
                Array.from({ length: 12 }).map((_, i) => <CardSkeleton key={i} />)
              ) : (
                data.map((item) => {
                  const formattedRating =
                    typeof item.rating?.average === "number" && Number.isFinite(item.rating.average) ? item.rating.average.toFixed(1) : "—";
                  const formattedTotalRatings =
                    typeof item.rating?.total_rating === "number" && Number.isFinite(item.rating.total_rating)
                      ? item.rating.total_rating.toLocaleString()
                      : null;
                  const formattedGenres = Array.isArray(item.genre)
                    ? item.genre.filter(Boolean).join(", ")
                    : item.genre
                      ? String(item.genre)
                      : "Unknown";
                  const formattedPrice = (() => {
                    const amount = item?.price?.amount;
                    const currency = item?.price?.currency;
                    if (typeof amount !== "number" || !Number.isFinite(amount)) return null;
                    if (amount === 0) return "Free";
                    const symbol = currency === "USD" || !currency ? "$" : `${currency} `;
                    return `${symbol}${amount.toFixed(2)}`;
                  })();                  const genreSlug = Array.isArray(item.genre) && item.genre.length > 0 
                    ? generateSlug(item.genre[0]) 
                    : item.genre && typeof item.genre === 'string' 
                      ? generateSlug(item.genre) 
                      : "app";
                  const nameSlug = generateSlug(item.name);
                  const appLink = `/xr/${genreSlug}/${nameSlug}?id=${item.item_id}`;
                  const platformName = item.platform ? getPlatformDisplayName(item.platform) : null;
                  const platformIconUrl = item.platform ? getPlatformIcon(item.platform) : null;

                  return (
                    <Link key={item.item_id} to={appLink} className="experience-card" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                      <div className="position-relative">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          style={{ aspectRatio: "16/10", objectFit: "cover", width: "100%", display: 'block' }}
                        />
                      </div>
                      <div className="card-body p-3">
                        <h6 className="card-title mb-2 text-truncate text-white" style={{ fontSize: 15, fontWeight: 600 }}>{item.name}</h6>
                        <div className="d-flex align-items-center justify-content-between" style={{ fontSize: 13, gap: 8 }}>
                          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                            <span style={{ color: '#ffd166' }}>★</span>
                            <span className="text-white">
                              {formattedRating}
                              {formattedTotalRatings ? (
                                <span style={{ color: "rgba(255,255,255,0.4)", marginLeft: 4 }}>({formattedTotalRatings})</span>
                              ) : null}
                            </span>
                          </div>
                          {formattedPrice ? (
                            <span style={{ whiteSpace: "nowrap", fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>
                              {formattedPrice}
                            </span>
                          ) : null}
                        </div>
                        {/* Platform — which store (Meta Quest / Steam / Apple Vision)
                            this result is from. Lives with the rest of the details
                            below the artwork rather than over it. */}
                        {platformName && (
                          <div className="d-flex align-items-center gap-1" style={{ fontSize: 12, marginTop: 6, color: "rgba(255,255,255,0.7)" }}>
                            {platformIconUrl && (
                              <img src={platformIconUrl} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                            )}
                            {platformName}
                          </div>
                        )}
                        <div className="text-truncate" title={formattedGenres} style={{ fontSize: 12, marginTop: 6, color: "rgba(255,255,255,0.4)" }}>
                          {formattedGenres}
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {total > pageSize && (
              <div className="pagination-container">
                <button
                  className="pagination-btn"
                  type="button"
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={page <= 1}
                >
                  Prev
                </button>

                {getPageNumbers(page, Math.ceil(total / pageSize)).map((pg, idx) => {
                  if (pg === "...") {
                    return (
                      <span key={`ellipsis-${idx}`} style={{ color: "rgba(255,255,255,0.4)", padding: '0 8px' }}>...</span>
                    );
                  }
                  return (
                    <button
                      key={`pg-${pg}`}
                      type="button"
                      className={`pagination-btn ${page === pg ? 'active' : ''}`}
                      onClick={() => {
                        setPage(pg);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      {pg}
                    </button>
                  );
                })}

                <button
                  className="pagination-btn"
                  type="button"
                  onClick={() => {
                    setPage((p) => Math.min(Math.ceil(total / pageSize), p + 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={page >= Math.ceil(total / pageSize)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
